/**
 * Resolves the effective polycount range ({ minimum, maximum, default }) from a
 * face_count / target_polycount schema node. When the node declares a `oneOf`,
 * the branch whose `if.properties` all match the current model settings wins;
 * this supports branches keyed on `polygon_type` (tripo3d, hyper3d) and on
 * `smart_low_poly` (meshy image-to-3d, tripo3d). Branches are evaluated in
 * order, so a schema mixing both keys must declare its `smart_low_poly` branch
 * first. Falls back to the node itself when there is no `oneOf` or no branch
 * matches.
 *
 * @param {Object} schema - The face_count or target_polycount schema node.
 * @param {string} schemaKey - Either "face_count" or "target_polycount".
 * @param {Object} settings - { polygonType, isSmartLowPoly } from model settings.
 * @returns {Object|undefined} The resolved range node, or undefined when no schema.
 */
export function resolvePolycountRange(schema, schemaKey, { polygonType, isSmartLowPoly } = {}) {
  if (!schema) {
    return undefined
  }

  if (!schema.oneOf) {
    return schema
  }

  const values = { polygon_type: polygonType, smart_low_poly: isSmartLowPoly }
  const matched = schema.oneOf.find((entry) => {
    const conditions = entry?.if?.properties || {}

    return Object.entries(conditions).every(([key, condition]) => (
      !('const' in condition) || values[key] === condition.const
    ))
  })

  if (matched) {
    return matched?.then?.properties?.[schemaKey]
  }

  return schema
}
